import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, CheckCircle, XCircle, Clock, Download, ChevronDown, ChevronUp,
  AlertTriangle, Utensils, Baby, Mail, UserPlus, BarChart3, RefreshCcw, Music
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Stat Card ──
function StatCard({ icon: Icon, label, value, color, sub }) {
  const colorMap = {
    green: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
    red: 'text-red-500 bg-red-50 dark:bg-red-900/20 dark:text-red-400 border-red-200 dark:border-red-800',
    amber: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    blue: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    purple: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    gray: 'text-gray-600 bg-gray-50 dark:bg-gray-800/40 dark:text-gray-300 border-gray-200 dark:border-gray-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color] || colorMap.gray}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 text-sm mb-1 opacity-80">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs mt-0.5 opacity-60">{sub}</p>}
    </div>
  );
}

// ── Progress Ring (SVG donut) ──
function DonutChart({ confirmed, declined, noResponse }) {
  const total = confirmed + declined + noResponse;
  if (total === 0) return null;
  const r = 54, cx = 64, cy = 64, circ = 2 * Math.PI * r;
  const segments = [
    { value: confirmed, color: '#10b981' },
    { value: declined, color: '#ef4444' },
    { value: noResponse, color: '#d1d5db' },
  ];
  let offset = 0;
  return (
    <div className="flex items-center gap-6">
      <svg width="128" height="128" viewBox="0 0 128 128" data-testid="rsvp-donut-chart">
        {segments.map((seg, i) => {
          const pct = seg.value / total;
          const dash = pct * circ;
          const gap = circ - dash;
          const el = (
            <circle
              key={i} cx={cx} cy={cy} r={r} fill="none"
              stroke={seg.color} strokeWidth="16"
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${cx} ${cy})`}
              className="transition-all duration-700"
            />
          );
          offset += dash;
          return el;
        })}
        <text x={cx} y={cy - 6} textAnchor="middle" className="fill-current text-gray-800 dark:text-gray-200" fontSize="22" fontWeight="bold">{total}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" className="fill-current text-gray-500 dark:text-gray-400" fontSize="10">responses</text>
      </svg>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" />
          <span className="text-gray-700 dark:text-gray-300">Confirmed ({confirmed})</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
          <span className="text-gray-700 dark:text-gray-300">Declined ({declined})</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600 inline-block" />
          <span className="text-gray-700 dark:text-gray-300">No response ({noResponse})</span>
        </div>
      </div>
    </div>
  );
}

// ── Timeline Bar Chart ──
function TimelineChart({ data }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.confirmed + d.declined), 1);
  return (
    <div className="space-y-2" data-testid="rsvp-timeline-chart">
      {data.map((d, i) => {
        const total = d.confirmed + d.declined;
        return (
          <div key={d.date} className="flex items-center gap-3 text-xs">
            <span className="w-20 text-gray-500 dark:text-gray-400 text-right shrink-0">
              {new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
            <div className="flex-1 flex items-center gap-0.5 h-5">
              {d.confirmed > 0 && (
                <div
                  className="h-full bg-emerald-500 rounded-l transition-all duration-500"
                  style={{ width: `${(d.confirmed / maxVal) * 100}%`, minWidth: 4 }}
                  title={`${d.confirmed} confirmed`}
                />
              )}
              {d.declined > 0 && (
                <div
                  className="h-full bg-red-400 rounded-r transition-all duration-500"
                  style={{ width: `${(d.declined / maxVal) * 100}%`, minWidth: 4 }}
                  title={`${d.declined} declined`}
                />
              )}
            </div>
            <span className="w-8 text-gray-600 dark:text-gray-400">{total}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Collapsible Section ──
function Section({ title, icon: Icon, count, defaultOpen, children, testId }) {
  const [open, setOpen] = useState(defaultOpen ?? true);
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden" data-testid={testId}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-2.5 text-sm font-semibold text-gray-800 dark:text-gray-200">
          <Icon className="w-4 h-4" />
          {title}
          {count != null && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {count}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-800">{children}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function RSVPDashboard({ eventId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/events/${eventId}/rsvp-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load RSVP dashboard:', err);
      toast.error('Failed to load RSVP data');
    } finally {
      setLoading(false);
    }
  }, [eventId, token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // ── CSV Export ──
  const exportCSV = useCallback(() => {
    if (!data?.responses) return;
    const headers = ['Name', 'Status', 'Guests', 'Email', 'Phone', 'Dietary', 'Allergies', 'Children', 'Song', 'Message', 'Date'];
    const rows = data.responses.map(r => [
      r.name,
      r.attending ? 'Confirmed' : 'Declined',
      r.guest_count || 1,
      r.email || '',
      r.phone || '',
      (r.dietary_preferences?.join(', ') || r.dietary || ''),
      r.allergies || '',
      r.children_coming ? `Yes (${r.children_count || 0})` : 'No',
      r.song_suggestion || '',
      (r.message || '').replace(/"/g, '""'),
      r.created_at ? new Date(r.created_at).toLocaleDateString() : '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rsvp-responses-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="rsvp-dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-200" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16 text-gray-500 dark:text-gray-400" data-testid="rsvp-dashboard-empty">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="font-medium">No RSVP data yet</p>
        <p className="text-sm mt-1">Publish your invitation and share the link to start collecting responses.</p>
      </div>
    );
  }

  const { stats, no_response_guests, new_from_rsvp, dietary_breakdown, timeline, responses } = data;

  return (
    <div className="space-y-6" data-testid="rsvp-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">RSVP Dashboard</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {stats.total_on_guest_list} guests on list &middot; {stats.total_responses} responses received
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchDashboard} data-testid="rsvp-refresh-btn">
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="rsvp-export-csv-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={CheckCircle} label="Confirmed" value={stats.confirmed} color="green" />
        <StatCard icon={XCircle} label="Declined" value={stats.declined} color="red" />
        <StatCard icon={Clock} label="No Response" value={stats.no_response} color="amber" />
        <StatCard icon={Users} label="Total Guests" value={stats.total_guests} color="blue" sub="attending + plus-ones" />
        <StatCard icon={Baby} label="Children" value={stats.children_count} color="purple" />
        <StatCard icon={UserPlus} label="New from RSVP" value={stats.new_from_rsvp} color="gray" sub="not on original list" />
      </div>

      {/* Donut Chart + Timeline */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Response Breakdown
          </h3>
          <DonutChart confirmed={stats.confirmed} declined={stats.declined} noResponse={stats.no_response} />
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Response Timeline
          </h3>
          {timeline.length > 0 ? (
            <TimelineChart data={timeline} />
          ) : (
            <p className="text-sm text-gray-400 py-8 text-center">No timeline data yet</p>
          )}
        </div>
      </div>

      {/* Who Hasn't Replied */}
      <Section title="Who Hasn't Replied Yet" icon={AlertTriangle} count={no_response_guests.length} defaultOpen={no_response_guests.length > 0 && no_response_guests.length <= 30} testId="section-no-response">
        {no_response_guests.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Everyone on the guest list has responded!</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b dark:border-gray-700">
                  <th className="pb-2 pr-4">Name</th>
                  <th className="pb-2 pr-4">Email</th>
                  <th className="pb-2 pr-4">Group</th>
                  <th className="pb-2">Table</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {no_response_guests.map(g => (
                  <tr key={g.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    <td className="py-2.5 pr-4 text-gray-800 dark:text-gray-200 font-medium">{g.full_name}</td>
                    <td className="py-2.5 pr-4 text-gray-500 dark:text-gray-400">{g.email || '-'}</td>
                    <td className="py-2.5 pr-4 text-gray-500 dark:text-gray-400">{g.group_name || '-'}</td>
                    <td className="py-2.5 text-gray-500 dark:text-gray-400">{g.table_name || 'Unassigned'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* New Guests from RSVP */}
      {new_from_rsvp.length > 0 && (
        <Section title="New Guests from RSVP" icon={UserPlus} count={new_from_rsvp.length} defaultOpen testId="section-new-from-rsvp">
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 mb-2">These people responded but weren't on your original guest list. They need table assignments.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b dark:border-gray-700">
                  <th className="pb-2 pr-4">Name</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4">Guests</th>
                  <th className="pb-2 pr-4">Table</th>
                  <th className="pb-2">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {new_from_rsvp.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    <td className="py-2.5 pr-4 text-gray-800 dark:text-gray-200 font-medium">{r.name}</td>
                    <td className="py-2.5 pr-4">
                      {r.attending ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs font-medium"><CheckCircle className="w-3.5 h-3.5" /> Yes</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-500 text-xs font-medium"><XCircle className="w-3.5 h-3.5" /> No</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-400">
                      <span>{r.guest_count}</span>
                      {r.additional_guests?.length > 0 && (
                        <div className="mt-0.5">
                          {r.additional_guests.map((g, j) => (
                            <span key={j} className="block text-[11px] text-gray-400 dark:text-gray-500">{g.name || `Guest ${j + 2}`}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-500 dark:text-gray-400">{r.table_name || 'Unassigned'}</td>
                    <td className="py-2.5 text-gray-500 dark:text-gray-400 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Dietary Breakdown */}
      <Section title="Dietary Summary" icon={Utensils} count={dietary_breakdown.length} defaultOpen={dietary_breakdown.length > 0} testId="section-dietary">
        {dietary_breakdown.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No dietary data collected yet</p>
        ) : (
          <div className="mt-3 space-y-2">
            {dietary_breakdown.map((d, i) => {
              const max = dietary_breakdown[0]?.count || 1;
              return (
                <div key={d.label} className="flex items-center gap-3">
                  <span className="w-32 text-sm text-gray-700 dark:text-gray-300 truncate text-right">{d.label}</span>
                  <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-emerald-500/80 dark:bg-emerald-600 transition-all duration-500 flex items-center justify-end pr-2"
                      style={{ width: `${Math.max(8, (d.count / max) * 100)}%` }}
                    >
                      <span className="text-[10px] font-bold text-white">{d.count}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Song Requests */}
      {(() => {
        const songs = responses.filter(r => r.song_suggestion);
        return songs.length > 0 ? (
          <Section title="Song Requests" icon={Music} count={songs.length} defaultOpen testId="section-song-requests">
            <div className="mt-3 space-y-2">
              {songs.map((r, i) => (
                <div key={r.id || i} className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <Music className="w-4 h-4 text-purple-500 dark:text-purple-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{r.song_suggestion}</p>
                    <p className="text-xs text-gray-400">by {r.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        ) : null;
      })()}

      {/* Additional Guest Details */}
      {(() => {
        const withGuests = responses.filter(r => r.additional_guests?.length > 0);
        return withGuests.length > 0 ? (
          <Section title="Plus-One Details" icon={UserPlus} count={withGuests.reduce((s, r) => s + r.additional_guests.length, 0)} defaultOpen={false} testId="section-plus-one-details">
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b dark:border-gray-700">
                    <th className="pb-2 pr-3">Primary Guest</th>
                    <th className="pb-2 pr-3">Plus-One Name</th>
                    <th className="pb-2">Dietary</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {withGuests.flatMap(r =>
                    r.additional_guests.map((g, i) => (
                      <tr key={`${r.id}-${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                        <td className="py-2 pr-3 text-gray-600 dark:text-gray-400">{i === 0 ? r.name : ''}</td>
                        <td className="py-2 pr-3 text-gray-800 dark:text-gray-200 font-medium">{g.name || `Guest ${i + 2}`}</td>
                        <td className="py-2 text-gray-500 dark:text-gray-400 text-xs">{g.dietary || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Section>
        ) : null;
      })()}

      {/* All Responses Table */}
      <Section title="All Responses" icon={Users} count={responses.length} defaultOpen={false} testId="section-all-responses">
        {responses.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No responses yet</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b dark:border-gray-700">
                  <th className="pb-2 pr-3">Name</th>
                  <th className="pb-2 pr-3">Status</th>
                  <th className="pb-2 pr-3">Guests</th>
                  <th className="pb-2 pr-3">Email</th>
                  <th className="pb-2 pr-3">Phone</th>
                  <th className="pb-2 pr-3">Dietary</th>
                  <th className="pb-2 pr-3">Message</th>
                  <th className="pb-2">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {responses.map(r => (
                  <tr key={r.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/40 ${r.is_duplicate_flag ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                    <td className="py-2.5 pr-3 text-gray-800 dark:text-gray-200 font-medium whitespace-nowrap">
                      {r.name}
                      {r.is_duplicate_flag && (
                        <span className="ml-1.5 text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded">dup</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      {r.attending ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs font-medium"><CheckCircle className="w-3.5 h-3.5" /> Yes</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-500 text-xs font-medium"><XCircle className="w-3.5 h-3.5" /> No</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-400">
                      <span>{r.guest_count || 1}</span>
                      {r.additional_guests?.length > 0 && (
                        <div className="mt-0.5">
                          {r.additional_guests.map((g, j) => (
                            <span key={j} className="block text-[11px] text-gray-400 dark:text-gray-500">{g.name || `Guest ${j + 2}`}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-500 dark:text-gray-400 text-xs">{r.email || '-'}</td>
                    <td className="py-2.5 pr-3 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">{r.phone || '-'}</td>
                    <td className="py-2.5 pr-3 text-gray-500 dark:text-gray-400 text-xs max-w-[120px] truncate">
                      {r.dietary_preferences?.join(', ') || r.dietary || '-'}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-500 dark:text-gray-400 text-xs max-w-[160px] truncate">{r.message || '-'}</td>
                    <td className="py-2.5 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
